import { TsDOM as q } from '@/scripts/core/TsDOM'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import { type ModalDialog } from '@/scripts/theme/modal-dialog'
import { motionEnabled } from '@/scripts/core/motion'

let signupAutoOpened = false

export class LandingPolicyController extends UcoastEl {
	static htmlSelector = 'landing-policy-controller'
	private readonly modals = new Map<string, ModalDialog>()
	private readonly observers: MutationObserver[] = []
	private resizeObserver?: ResizeObserver
	private resizeAnimation?: Animation

	override connectedCallback() {
		super.connectedCallback()
		this.observeSignupSize()
		document
			.querySelectorAll<ModalDialog>('modal-dialog[data-url-hash]')
			.forEach((modal) => this.registerModal(modal))
		window.addEventListener('hashchange', this.syncHash)
		window.queueMicrotask(() => {
			this.openSignup()
			this.syncHash()
		})
	}

	private observeSignupSize() {
		const selector = this.dataset.autoOpenModal
		if (!selector) return
		const modal = q.os<ModalDialog>(selector)
		const content = modal?.querySelector<HTMLElement>('.landing-hero__modal-content')
		if (!modal || !content) return

		let previousHeight = content.getBoundingClientRect().height
		this.resizeObserver = new ResizeObserver(() => {
			const height = content.getBoundingClientRect().height
			const delta = height - previousHeight
			previousHeight = height
			if (Math.abs(delta) < 1) return

			const styles = getComputedStyle(modal)
			const targetHeight = height + parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom)
			const currentHeight = this.resizeAnimation?.playState === 'running'
				? modal.getBoundingClientRect().height
				: targetHeight - delta
			this.resizeAnimation?.cancel()
			if (!motionEnabled() || !modal.hasAttribute('open')) return

			this.resizeAnimation = modal.animate(
				[{ height: `${currentHeight}px` }, { height: `${targetHeight}px` }],
				{ duration: 250, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' }
			)
		})
		this.resizeObserver.observe(content)
	}

	private openSignup() {
		if (signupAutoOpened) return
		const modalSelector = this.dataset.autoOpenModal
		if (!modalSelector) return
		const modal = q.os<ModalDialog>(modalSelector)
		const opener = q.os<HTMLElement>(
			`modal-opener[data-modal="${modalSelector}"] button`
		)
		if (!modal || !opener) return

		signupAutoOpened = true
		modal.setAttribute('data-auto-open', '')
		const observer = new MutationObserver(() => {
			if (modal.hasAttribute('open')) return
			modal.removeAttribute('data-auto-open')
			observer.disconnect()
		})
		observer.observe(modal, {
			attributes: true,
			attributeFilter: ['open'],
		})
		this.observers.push(observer)
		modal.show(opener)
	}

	private registerModal(modal: ModalDialog) {
		const hash = modal.dataset.urlHash
		if (!hash) return

		this.modals.set(hash, modal)
		document.querySelectorAll<HTMLElement>(`a[href="#${hash}"]`).forEach((link) => {
			link.setAttribute('aria-haspopup', 'dialog')
			link.setAttribute('aria-controls', modal.id)
		})
		const observer = new MutationObserver(() => {
			if (
				!modal.hasAttribute('open') &&
				window.location.hash === `#${hash}`
			) {
				window.history.replaceState(
					window.history.state,
					'',
					`${window.location.pathname}${window.location.search}`
				)
			}
		})
		observer.observe(modal, {
			attributes: true,
			attributeFilter: ['open'],
		})
		this.observers.push(observer)
	}

	private syncHash = () => {
		this.modals.forEach((modal, hash) => {
			const isTarget = window.location.hash === `#${hash}`
			if (!isTarget) {
				if (modal.hasAttribute('open')) modal.hide(true)
				return
			}
			if (modal.hasAttribute('open')) return

			const link = q.os<HTMLElement>(`a[href="#${hash}"]`)
			const opener = link && link.offsetParent !== null ? link : document.body
			const activeModal = q.os<ModalDialog>('modal-dialog[open]')
			if (activeModal?.classList.contains('landing-hero__modal')) {
				modal.show(opener)
				return
			}
			if (activeModal && activeModal !== modal) {
				document.body.addEventListener(
					'modalClosed',
					() => {
						if (window.location.hash === `#${hash}`)
							modal.show(opener)
					},
					{ once: true }
				)
				activeModal.hide(true)
				return
			}

			modal.show(opener)
		})
	}

	override disconnectedCallback() {
		this.resizeObserver?.disconnect()
		this.resizeAnimation?.cancel()
		window.removeEventListener('hashchange', this.syncHash)
		this.observers.forEach((observer) => observer.disconnect())
		this.observers.length = 0
		super.disconnectedCallback()
	}
}
